import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { Toaster, toast } from 'react-hot-toast'

import { registerSW } from 'virtual:pwa-register'

import App from './App'
import GoogleMapsProvider from './components/maps/GoogleMapsProvider'
import i18n from './i18n'
import './index.css'

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Auto-reload on chunk loading failures (after deployments)
window.addEventListener('unhandledrejection', (event) => {
  const msg = String(event.reason?.message || event.reason || '')
  if (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('Loading chunk')
  ) {
    const key = 'immotron-chunk-reload'
    const last = sessionStorage.getItem(key)
    if (!last || Date.now() - Number(last) > 10000) {
      sessionStorage.setItem(key, String(Date.now()))
      window.location.reload()
    }
  }
})

// Register PWA Service Worker (injectManifest strategy)
// onNeedRefresh: show a non-blocking toast so the user can decide when to
// reload. A hard reload here would race the runtime cache and yield a
// blank/slow page right after deploy.
registerSW({
  immediate: true,
  onNeedRefresh() {
    toast(
      (t) => (
        <span className="flex items-center gap-3">
          <span>{i18n.t('pwa.newVersionAvailable')}</span>
          <button
            type="button"
            onClick={() => {
              toast.dismiss(t.id)
              window.location.reload()
            }}
            className="rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {i18n.t('pwa.update')}
          </button>
        </span>
      ),
      { duration: Infinity, id: 'pwa-update' }
    )
  },
})

// Handle SW-initiated navigation (from push notification click)
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'NAVIGATE' && event.data.url) {
      window.location.href = event.data.url
    }
  })
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <GoogleMapsProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#1e293b',
                color: '#f8fafc',
              },
              success: {
                iconTheme: {
                  primary: '#22c55e',
                  secondary: '#f8fafc',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#f8fafc',
                },
              },
            }}
          />
        </BrowserRouter>
      </QueryClientProvider>
    </GoogleMapsProvider>
  </React.StrictMode>
)
